import React, { useState, useRef, useEffect } from 'react';
import { MDBModal, MDBModalBody, MDBModalHeader } from 'mdbreact';
import { useDispatch, useSelector } from 'react-redux';
import addNotification from 'helpers/notify';
import { useHttp } from 'hooks/useHttp';
import { uploadFileToServer } from 'components/uploadFile/uploadFile';
import { createPost, updatePostBack } from 'api/posts/createPost';
import { addNewPost } from 'actions/posts';
import { 
  updatePreviewData, 
  resetPreviewData, 
  updatePreviewMedia,
  updatePreviewContent,
  setPreviewPlatform 
} from 'actions/preview';
import Loading from 'components/ui-components/loading';
import { SocialPreview } from 'components/SocialMediaPreviewTemplates/SocialPreview/SocialPreview';
import {previewData} from 'components/SocialMediaPreviewTemplates/previewData/previewData';
import CommonSocialPreview from 'components/SocialMediaPreviewTemplates/CommonSocialPreview/CommonSocialPreview';
import './CreatePostModal.scss';
import { setSelectedPost, updatePost, updateBrandPost, updatePostStatus, updateAllPostsStatus } from 'actions/posts';
import { editPost } from 'api/brands/editPost';
import { modalArchiveControl, ARCHIVED_STATUS } from 'helpers/postArchive';
import moment from 'moment';
import { SocialAccountConfigurations } from './socialMediaConfig';
import { PLATFORM_COLORS, PLATFORM_GRADIENT, selectedPlatformsForPost, normalizeMediaUrl, resolveConnectedSocialAccounts, mergeBrandSocialAccounts, targetedAccountIds, resolveTargetedAccountIds } from 'helpers/postDisplay';
import { resolvePreviewAuthor, pickPreviewAccount } from 'helpers/previewAuthor';
import TikTokPostOptions from 'components/TikTokPostOptions/TikTokPostOptions';
import { fetchTikTokCreatorInfo } from 'api/social/tiktokCreatorInfo';

const CreatePostModal = ({ isOpen, toggle, id  }) => {
  const dispatch = useDispatch();
  const userInfoRedux = useSelector(s => s.user.userInfo);
  const selectedBrandRedux = useSelector(s => s.post.selectedBrand)
  const previewData = useSelector(s => s.preview);
  const selectedPlatform = useSelector(s => s.preview.selectedPlatform);
  const selectedPost = useSelector(s => s.post.selectedPost);
  const brandsRedux = useSelector(s => s.user.brands);
  const ideasRedux = useSelector((s) => s.ideas?.ideas);
  const [accountData, setAccountData] = useState(null);

  const accountsRedux = [
    {
      platform: 'linkedin',
      name: 'LinkedIn',
      iconClass: "bi bi-linkedin",
      color: '#0077b5',
      icon: 'fab fa-linkedin',
      status: 'active',
    },
    {
      platform: 'twitter',
      name: 'Twitter',
      iconClass: "bi bi-twitter",
      color: '#1DA1F2',
      icon: 'fab fa-twitter',
      status: 'active',
    },
    {
      platform: 'facebook',
      name: 'Facebook', 
      iconClass: "bi bi-facebook",
      color: '#4267B2',
      icon: 'fab fa-facebook',
      status: 'inactive',
    },
    {
      platform: 'pinterest',
      name: 'Pinterest',
      iconClass: "bi bi-pinterest",
      color: '#E60023',
      icon: 'fab fa-pinterest',
      status: 'active',
    },
    {
      platform: 'youtube',
      name: 'YouTube',
      iconClass: "bi bi-youtube",
      color: '#FF0000',
      icon: 'fab fa-youtube',
      status: 'active',
    },
    {
      platform: 'instagram',
      name: 'Instagram',
      iconClass: "bi bi-instagram",
      color: '#E4405F',
      icon: 'fab fa-instagram',
      status: 'pending',
    },
    {
      platform: 'snapchat',
      name: 'Snapchat',
      iconClass: "bi bi-snapchat",
      color: '#FFFC00',
      icon: 'fab fa-snapchat',
      status: 'inactive',
    },
    {
      platform: 'google',
      name: 'Google',
      iconClass: "bi bi-google",
      color: '#4285F4',
      icon: 'fab fa-google',
      status: 'active',
    },
    {
      platform: 'tiktok',
      name: 'TikTok',
      iconClass: "bi bi-tiktok",
      color: '#000000',
      icon: 'fab fa-tiktok',
      status: 'active',
    }
  ];

  // The brand's connected accounts drive which platform tiles are
  // enabled. selectedBrand isn't always populated when the modal opens
  // from a post (the onEdit handlers only set selectedPost), which used
  // to grey out every tile — so fall back to the post's own brand from
  // the brands list, and to the post's targeted accounts. accountName is
  // set on OAuth callback to the real profile name; title is only the
  // platform-slug fallback, so resolveConnectedSocialAccounts prefers
  // accountName. user_id is propagated so the preview-author selection
  // below can scope to the logged-in user's connected account.
  // Union the selectedBrand's accounts (loaded when the brand was opened) with
  // the live brands tree (refreshed as platforms are connected via OAuth), so a
  // platform connected AFTER posts were created still shows up as available and
  // can be added in the preview. Preferring only selectedBrand used to hide
  // newly connected platforms.
  const brandIdForPost = String(selectedPost?.brand_id || selectedBrandRedux?._id || '');
  const liveBrandAccounts = (brandsRedux || [])
    .find(b => String(b._id) === brandIdForPost)?.brandSettings?.socialAccounts;
  const brandSocialAccounts = mergeBrandSocialAccounts(
    selectedBrandRedux?.brandSettings?.socialAccounts,
    liveBrandAccounts,
  );

  const connectedAccounts = resolveConnectedSocialAccounts({
    baseAccounts: accountsRedux,
    brandSocialAccounts,
    postTargetedAccounts: selectedPost?.targetedSocialAccounts || [],
  });
  const imageInputRef = useRef(null);

  useEffect(() => {
    // Pick the connected social account whose profile drives the
    // "Review as" preview header. Logic lives in pickPreviewAccount
    // so the user-scoping rules can be unit-tested.
    setAccountData(pickPreviewAccount({
      socialAccounts: selectedBrandRedux?.brandSettings?.socialAccounts,
      platform: selectedPlatform,
      userId: userInfoRedux?._id,
    }));
  }, [selectedBrandRedux?.brandSettings?.socialAccounts, selectedPlatform, userInfoRedux?._id]);
  

  const [postContent, setPostContent] = useState('');
  const [title, setTitle] = useState('');
  const [postImage, setPostImage] = useState([]);
  const [selectedIdea, setSelectedIdea] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  // TikTok Direct Post selections. `build()` stamps consentedAt, so it is
  // called at SUBMIT and never on render — opening the form is not consent.
  const [tiktokChoice, setTiktokChoice] = useState(null);
  const [targetedPlatforms, setTargetedPlatforms] = useState([]);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  // Preserves the URLs of media that was already attached to a post
  // being edited. We re-send these on Save so the existing media isn't
  // dropped when the user only changes text/time/platforms.
  const [existingMedia, setExistingMedia] = useState({ images: [], videos: [] });

  useEffect(() => {
    if (selectedPost) {
      setTitle(selectedPost.title || '');

      // Moment's lowercase 'yyyy' is the ISO week year (different from
      // calendar year). HTML <input type="date"> expects YYYY-MM-DD,
      // so we use uppercase Y. Also guard against null scheduled_at —
      // a draft post may not have one yet → blank the fields instead
      // of formatting "Invalid date".
      if (selectedPost.scheduled_at) {
        const m = moment(selectedPost.scheduled_at);
        if (m.isValid()) {
          setScheduleDate(m.format('YYYY-MM-DD'));
          setScheduleTime(m.format('HH:mm'));
        } else {
          setScheduleDate(''); setScheduleTime('');
        }
      } else {
        setScheduleDate(''); setScheduleTime('');
      }

      setPostContent(selectedPost.textContent ?? '');
      dispatch(updatePreviewContent({
        mainText: selectedPost.textContent?.split('\n').filter(line => line.trim()),
        hashtags: selectedPost.textContent?.match(/#\w+/g) || []
      }));
      setSelectedIdea(selectedPost.ideaId || '');
      // Seed targeted accounts robustly: a post that was already edited+saved
      // carries bare id strings (PUT /posts doesn't populate), where reading
      // x._id would yield undefined and break re-toggling networks on Save.
      setTargetedPlatforms(targetedAccountIds(selectedPost));

      // Pre-fill the selectedPlatforms toggle bar from the post's
      // existing targeted accounts so the social-network icons render
      // active in their brand colors when the modal opens for edit.
      // Resolve via selectedPlatformsForPost so the active slugs stay in
      // lock-step with targetedPlatforms even when the post carries bare
      // id strings (PUT /posts shape) — postPlatforms() alone returns []
      // for those, which left the toggle bar blank and desynced from the
      // seeded targeted-account ids after the first edit+save.
      setSelectedPlatforms(selectedPlatformsForPost(selectedPost, brandSocialAccounts));

      // Surface existing media (imageContent / videoContent) in the
      // preview when editing. Without this, the preview pane is blank
      // until the user re-uploads — which loses the existing image
      // entirely when they Save.
      const images   = Array.isArray(selectedPost.imageContent) ? selectedPost.imageContent : [];
      const videos   = Array.isArray(selectedPost.videoContent) ? selectedPost.videoContent : [];
      // imageContent / videoContent rows store either a full URL or a
      // bare R2 key (`<uuid>.png`). The preview <img>/<video> tags need
      // a browser-resolvable URL — normalizeMediaUrl prepends /uploads/
      // for bare keys (express + r2UploadsProxy resolves them) and
      // passes full URLs through unchanged. Skip legacy .gz keys
      // (needsUnzip) since the preview doesn't unzip on the fly.
      const previewAttachments = [
        ...images.map((raw) => {
          const { url, needsUnzip } = normalizeMediaUrl(raw);
          return needsUnzip ? null : { type: 'image', url, aspectRatio: 'portrait', alt: 'image', existing: true };
        }).filter(Boolean),
        ...videos.map((raw) => {
          const { url, needsUnzip } = normalizeMediaUrl(raw);
          return needsUnzip ? null : { type: 'video', url, aspectRatio: 'portrait', alt: 'video', existing: true };
        }).filter(Boolean),
      ];
      if (previewAttachments.length) dispatch(updatePreviewMedia(previewAttachments));
      // Track existing URLs separately from postImage (File[]). When
      // the user submits without changing media, we'll re-send these.
      setExistingMedia({ images, videos });
    } else {
      setExistingMedia({ images: [], videos: [] });
      // New post (no selectedPost): pre-select every connected social
      // account for this brand so the user can't silently submit with
      // an empty platform set. Backend rejects empty targetedSocialAccounts
      // with a 400 that's easy to miss in the composer UI. User can still
      // untick any platform they don't want before Save. Union both account
      // sources so a freshly connected platform is pre-selected too.
      const newPostBrandId = String(selectedBrandRedux?._id || '');
      const newPostLiveAccounts = (brandsRedux || [])
        .find(b => String(b._id) === newPostBrandId)?.brandSettings?.socialAccounts;
      const connected = mergeBrandSocialAccounts(
        selectedBrandRedux?.brandSettings?.socialAccounts,
        newPostLiveAccounts,
      ).filter((a) => a.status === 'connected' && a.deleted !== true);
      setTargetedPlatforms(connected.map((a) => a._id));
      setSelectedPlatforms(connected.map((a) => a.platform).filter(Boolean));
    }
  }, [selectedPost, selectedBrandRedux, brandsRedux]);

  useEffect(() => {
    const authored = resolvePreviewAuthor(accountData, userInfoRedux);
    dispatch(updatePreviewData({
      author: {
        name: authored.name,
        image: authored.image,
        description: authored.description,
        isYou: true
      },
      // Top-level username/isVerified are read by SocialPreview templates
      // (LinkedIn/Twitter/Common) outside the author object — set them
      // here so the @handle and verified badge actually render.
      username: authored.handle,
      isVerified: Boolean(accountData?.verified),
      engagement: {
        reactions: [],
        comments: [],
        shares: 0,
        views: 0,
        likes: 0,
        replies: 0,
        impressions: 0
      }
    }));
  }, [accountData, userInfoRedux?.profilePhoto, userInfoRedux?.name, userInfoRedux?.lastName, userInfoRedux?.username]);

  const handlePlatformChange = (value) => {
    dispatch(setPreviewPlatform(value));
  };

  // Enforce per-platform limits PER TYPE (image vs video). Each
  // platform's socialMediaConfig entry carries `.post` (image cap)
  // and `.videos` (video cap). Twitter: 4 images OR 1 video. Instagram:
  // 10 images, 1 video. TikTok: 1 video. We tally what's currently
  // attached + what's about to be added and reject anything that would
  // exceed the lower bound across all selected platforms.
  const limitsAcrossSelected = () => {
    const platforms = selectedPlatforms.length ? selectedPlatforms : [selectedPlatform];
    let imageCap = Infinity;
    let videoCap = Infinity;
    for (const p of platforms) {
      const cfg = SocialAccountConfigurations[p];
      if (!cfg) continue;
      if (typeof cfg.post   === 'number') imageCap = Math.min(imageCap, cfg.post);
      if (typeof cfg.videos === 'number') videoCap = Math.min(videoCap, cfg.videos);
    }
    if (imageCap === Infinity) imageCap = 10; // sensible default if config silent
    if (videoCap === Infinity) videoCap = 1;
    return { imageCap, videoCap };
  };

  const refreshPreviewMedia = (newImages, existing) => {
    const fileAttachments = newImages.map((file) => ({
      type:  file.type.includes('image') ? 'image' : file.type.includes('video') ? 'video' : 'pdf',
      url:   URL.createObjectURL(file),
      aspectRatio: 'portrait',
      alt:   file.name,
    }));
    const existingAttachments = [
      ...(existing.images || []).map((url) => ({ type: 'image', url, aspectRatio: 'portrait', alt: 'image', existing: true })),
      ...(existing.videos || []).map((url) => ({ type: 'video', url, aspectRatio: 'portrait', alt: 'video', existing: true })),
    ];
    dispatch(updatePreviewMedia([...fileAttachments, ...existingAttachments]));
  };

  const handleImageUpload = async (e) => {
    const incoming = Array.from(e.target.files || []);
    if (!incoming.length) return;

    const { imageCap, videoCap } = limitsAcrossSelected();
    const currentImages = postImage.filter((f) => (f.type || '').includes('image')).length
                        + (existingMedia.images || []).length;
    const currentVideos = postImage.filter((f) => (f.type || '').includes('video')).length
                        + (existingMedia.videos || []).length;

    const accepted = [];
    let imgN = currentImages, vidN = currentVideos;
    for (const file of incoming) {
      const isImg = (file.type || '').includes('image');
      const isVid = (file.type || '').includes('video');
      if (isImg && imgN >= imageCap) {
        addNotification(`Image limit reached (${imageCap}) for the selected platform(s). Skipped: ${file.name}`, 'warning');
        continue;
      }
      if (isVid && vidN >= videoCap) {
        addNotification(`Video limit reached (${videoCap}) for the selected platform(s). Skipped: ${file.name}`, 'warning');
        continue;
      }
      accepted.push(file);
      if (isImg) imgN++; else if (isVid) vidN++;
    }
    if (!accepted.length) return;

    const next = [...accepted, ...postImage];
    setPostImage(next);
    refreshPreviewMedia(next, existingMedia);
    // Clear the input so re-selecting the same file fires onChange.
    if (e.target) e.target.value = '';
  };

  const handleRemoveUpload = (fileName) => {
    const remaining = postImage.filter((x) => x.name !== fileName);
    setPostImage(remaining);
    refreshPreviewMedia(remaining, existingMedia);
  };

  // Remove an EXISTING (already-uploaded) media URL from the post
  // being edited. Drops it from existingMedia AND from the preview;
  // on save, the dropped URL won't be re-sent so the backend sees
  // the up-to-date media list.
  const handleRemoveExisting = (url, kind) => {
    const key = kind === 'image' ? 'images' : 'videos';
    const nextExisting = {
      ...existingMedia,
      [key]: (existingMedia[key] || []).filter((u) => u !== url),
    };
    setExistingMedia(nextExisting);
    refreshPreviewMedia(postImage, nextExisting);
  };

  const togglePlatform = (platformId) => {
    setSelectedPlatforms(prev => 
      prev.includes(platformId) 
        ? prev.filter(p => p !== platformId)
        : [...prev, platformId]
    );
  };

  const { loading: createLoading, request: createPostAPI } = useHttp({
    requestCallback: (data) => createPost(data),
    onLoad: (res) => {
      dispatch(addNewPost(res));
      addNotification('Post created successfully!', 'success');
      handleClose();
    },
    onError: (error) => {
      addNotification(error.message || 'Error creating post', 'danger');
    }
  });
  const { loading: updateLoading, request: updatePostAPI } = useHttp({
    requestCallback: (data) => updatePostBack(data),
    onLoad: (res) => {
      // updatePost patches state.post.posts (kanban/calendar/list that
      // read from the post slice). updateBrandPost mirrors the same edit
      // into the big structure (state.user.brands[].posts + the derived
      // state.user.allPosts) so AllPostsPage and the brand tree-view
      // reflect the change live, without a page refresh.
      dispatch(updatePost(res));
      dispatch(updateBrandPost(res));
      addNotification('Post updated successfully!', 'success');
      handleClose();
    },
    onError: (error) => {
      addNotification(error.message || 'Error updating post', 'danger');
    }
  });

  const handleContentChange = (e) => {
    const content = e.target.value;
    setPostContent(content);
    dispatch(updatePreviewContent({
      mainText: content.split('\n').filter(line => line.trim()),
      hashtags: content.match(/#\w+/g) || []
    }));
  };

  const handleSubmit = async (type) => {
    try {
      // Derive the targeted account ids from the SAME source the tiles use
      // to render "active" — the resolved connectedAccounts intersected with
      // the selectedPlatforms slug stack. Reading the separately-tracked
      // targetedPlatforms state here is what produced the reported bug: a
      // network tile showed active (its slug was in selectedPlatforms) while
      // targetedPlatforms was empty, so scheduling failed with "no social
      // media selected". Sourcing both the validation and the payload from the
      // active tiles keeps them in lock-step. Fall back to the toggled
      // targetedPlatforms state for any active slug we couldn't resolve to an
      // account _id, so nothing the user ticked is silently dropped.
      const resolvedTargetedAccounts = resolveTargetedAccountIds(connectedAccounts, selectedPlatforms);
      const targetedAccounts = resolvedTargetedAccounts.length
        ? resolvedTargetedAccounts
        : (Array.isArray(targetedPlatforms) ? targetedPlatforms.filter(Boolean) : []);

      // Inline guard: backend rejects empty targetedSocialAccounts with
      // a 400 the user can't see clearly. Catch it client-side so the
      // failure is obvious instead of silent.
      if (targetedAccounts.length === 0) {
        addNotification('Pick at least one platform before saving.', 'danger');
        return;
      }
      setUploadLoading(true);
      
      const createObject = {
        brand_id: id,
        title: title,
        textContent: postContent,
        isText: true,
        ideaId: selectedIdea,
        platforms: selectedPlatforms,
        // Convert the date+time the user picked in their LOCAL timezone
        // to a proper UTC ISO string. The previous code sent a naive
        // "2026-05-14T14:45" which the server parsed in server-local tz
        // (UTC), so a Bangladesh user picking 14:45 had their post
        // stored as 14:45 UTC = 20:45 Bangladesh — and the display layer
        // (which formats UTC → browser local) then showed 20:45 forever.
        scheduleDate: scheduleDate
          ? new Date(`${scheduleDate}T${scheduleTime || '00:00'}`).toISOString()
          : null,
        companyId: userInfoRedux?.company,
        targetedSocialAccounts: targetedAccounts,
        isDraft: type === 'draft',
        type: selectedBrandRedux?.brandSettings?.postingType,
        // TikTok Direct Post: carry the user's own selections onto the post.
        // build() stamps consentedAt HERE, at submit — that timestamp is the
        // express consent TikTok requires before an upload may start, and the
        // worker treats its presence as the switch between publishing to the
        // profile (DIRECT_POST) and dropping a draft in the creator's inbox
        // (MEDIA_UPLOAD). Never stamped on render.
        ...(selectedPlatforms.includes('tiktok') && tiktokChoice?.ready
          ? { tiktokPostInfo: tiktokChoice.build() }
          : {}),
      };

      // Re-send any media that was already attached when this is an
      // edit. The previous code dropped existing media entirely
      // because postImage starts empty on edit — only newly uploaded
      // files were sent, so saving any non-media change wiped the
      // original imageContent/videoContent server-side.
      const imageContent = [...(existingMedia.images || [])];
      const videoContent = [...(existingMedia.videos || [])];

      // Upload each NEW file individually. The previous bug passed
      // the whole postImage array to uploadFileToServer(), which
      // expects a single File — every iteration uploaded nothing
      // usable and returned the same name repeatedly, dropping the
      // media before it ever reached the platform.
      if (Array.isArray(postImage) && postImage.length > 0) {
        for (const file of postImage) {
          // noZip: post media has to render via <img> / <video> tags
          // across kanban / calendar / list previews — the zip-wrap
          // path stored a .gz key that those views couldn't display
          // because they don't unwrap.
          const url = await uploadFileToServer(file, false, { noZip: true });
          if (!url) continue; // upload failed; skip rather than push null
          if ((file.type || '').includes('image'))      imageContent.push(url);
          else if ((file.type || '').includes('video')) videoContent.push(url);
        }
      }
      createObject.imageContent = imageContent;
      createObject.videoContent = videoContent;

      if (!selectedPost) {
        await createPostAPI(createObject);
      } else {
        createObject._id = selectedPost._id;
        await updatePostAPI(createObject);
      }
      setUploadLoading(false);
    } catch (error) {
      setUploadLoading(false);
      addNotification(error.message || 'Error creating post', 'danger');
    }
  };

  // Archive (or un-archive) the post currently open for edit. Wired to the
  // header button that sits just before the (X) close icon, so a post opened
  // from anywhere — Calendar / List / Kanban / brand tree — can be sent to
  // the Archive shelf without first finding its per-card button. Mirrors
  // AllPostsPage.handleArchive: optimistically patch both the post slice
  // (Kanban/Calendar/List) and the allPosts slice (AllPostsPage) so the move
  // shows instantly, then persist queueStatus via PUT /posts. archiveControl
  // resolves the target, so a live post archives and an archived one goes
  // back to awaiting-approval — identical to the per-card control.
  const handleArchivePost = async () => {
    const control = modalArchiveControl(selectedPost);
    if (!control) return;
    const target = control.target;
    const postId = selectedPost._id;
    dispatch(updatePostStatus({ postId, queueStatus: target }));
    dispatch(updateAllPostsStatus({ postId, queueStatus: target }));
    try {
      await editPost({ id: postId, body: { queueStatus: target } });
      addNotification(target === ARCHIVED_STATUS ? 'Post archived' : 'Post sent back to awaiting approval', 'success');
    } catch (e) {
      addNotification("Could not update the post's archive status", 'danger');
    }
    handleClose();
  };

  const handleClose = () => {
    setTitle('');
    setPostContent('');
    setPostImage([]);
    setSelectedPlatforms(['linkedin']);
    setScheduleDate('');
    setScheduleTime('');
    dispatch(resetPreviewData());
    dispatch(setSelectedPost(null));
    toggle();
  };

  const renderPreview = () => {
    const previewProps = {...previewData,platform: selectedPlatform};


    


   return <SocialPreview previewData={previewProps} onRemoveAttachment={(file) => handleRemoveUpload(file)} />;
  // return <SocialPreview previewData={previewData[selectedPlatform]} />
  };

  return (
    <MDBModal 
      isOpen={isOpen} 
      toggle={handleClose} 
      size="lg"
      centered
      className="create-post-modal"
    >
      <MDBModalHeader toggle={handleClose}>
        <span className="modal-title">{selectedPost ? "Update Post" : "Create Post"}</span>
        {/* Archive control — only in edit mode (no post to archive when
            creating). MDBModalHeader renders the (X) close button AFTER its
            children, so placing this here puts it immediately before the X,
            as the ticket asks. Lets any post opened for edit be sent to the
            Archive shelf in one click. Label/target come from
            modalArchiveControl so an already-archived post offers un-archive. */}
        {(() => {
          const control = modalArchiveControl(selectedPost);
          if (!control) return null;
          return (
            <button
              type="button"
              className="btn-archive-post"
              title={control.title}
              onClick={handleArchivePost}
            >
              {control.label} {control.archived ? 'Un-archive' : 'Archive'}
            </button>
          );
        })()}
      </MDBModalHeader>
      
      <MDBModalBody>
        <div className="form-layout">
          <div className="form-section">
            <div className="section-title">Social Networks</div>
            <div className="social-icons">
              {connectedAccounts.map(platform => {
                const isActive  = selectedPlatforms.includes(platform.platform);
                const isEnabled = platform.status === 'active';
                // Brand colors from the shared map; instagram uses its
                // signature gradient when active.
                const accent =
                  PLATFORM_GRADIENT[platform.platform] ||
                  PLATFORM_COLORS[platform.platform] ||
                  platform.color || '#525252';
                const background = !isEnabled
                  ? '#f3f4f6'
                  : isActive
                    ? accent
                    : '#fff';
                const fg = !isEnabled ? '#9ca3af' : isActive ? '#fff' : (PLATFORM_COLORS[platform.platform] || platform.color || '#525252');
                const border = `2px solid ${isActive ? 'transparent' : (PLATFORM_COLORS[platform.platform] || platform.color || '#d1d5db')}`;
                return (
                  <div
                    key={platform.platform}
                    className={`social-icon ${isActive ? 'active' : ''} ${!isEnabled ? 'disabled' : ''}`}
                    title={platform.name + (isEnabled ? '' : ' (not connected)')}
                    style={{
                      background,
                      color: fg,
                      border,
                      cursor: isEnabled ? 'pointer' : 'not-allowed',
                      opacity: isEnabled ? 1 : 0.5,
                      transition: 'background 0.15s ease, color 0.15s ease, border-color 0.15s ease',
                    }}
                    onClick={() => {
                      if (!isEnabled) return;
                      togglePlatform(platform.platform);
                      setTargetedPlatforms((prev) => prev.includes(platform._id)
                        ? prev.filter(p => p !== platform._id)
                        : [...prev, platform._id]);
                    }}
                  >
                    <i className={platform.icon}></i>
                  </div>
                );
              })}
            </div>

            <div className="section-title">Post Title</div>
            <div className="date-time-section">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="section-title">Publication Date</div>
            <div className="date-time-section">
              <input
                type="date"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
              <input
                type="time"
                value={scheduleTime}
                onChange={(e) => setScheduleTime(e.target.value)}
              />
            </div>

            <div className="section-title">Post Text</div>
            <textarea
              value={postContent}
              onChange={handleContentChange}
              placeholder="Type your text here..."
            />

            {selectedBrandRedux?.brandSettings?.postingType !== 'text' && (<>
              <div className="section-title">Media Upload</div>
              <div
                className="upload-area"
                onClick={() => imageInputRef.current?.click()}
              >
                <input
                  ref={imageInputRef}
                  type="file"
                  // Accept BOTH images and videos so a post can mix
                  // media when the platform allows (Facebook, etc).
                  // Per-type caps are enforced in handleImageUpload
                  // against the selected platforms' configs.
                  accept="image/*,video/*"
                  multiple={true}
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
                <span>Upload Image(s) / Video(s)</span>
                {(() => {
                  const { imageCap, videoCap } = limitsAcrossSelected();
                  return (
                    <small style={{ display: 'block', color: '#888', marginTop: 4, fontSize: 11 }}>
                      Platform limit{selectedPlatforms.length > 1 ? 's (lowest across selected)' : ''}: up to {imageCap} image{imageCap === 1 ? '' : 's'} · up to {videoCap} video{videoCap === 1 ? '' : 's'}
                    </small>
                  );
                })()}
              </div>

              {/* Thumbnail strip — shows EVERY attached file (existing
                  URLs preserved on edit + newly uploaded File objects)
                  with a remove button per tile and a kind badge. Lets
                  the operator see exactly what media will go to the
                  platform without relying on the social-style preview. */}
              {(postImage.length > 0 || (existingMedia.images || []).length > 0 || (existingMedia.videos || []).length > 0) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                  {/* Existing media that came in with the post on edit */}
                  {(existingMedia.images || []).map((url, i) => (
                    <div key={'ei-' + i} style={{ position: 'relative', width: 72, height: 72, borderRadius: 6, overflow: 'hidden', background: '#000' }}>
                      <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                           onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      <span style={{ position: 'absolute', top: 2, left: 2, padding: '0 5px', background: 'rgba(0,0,0,0.55)', color: '#fff', borderRadius: 3, fontSize: 9, fontWeight: 600 }}>IMG</span>
                      <button type="button" onClick={() => handleRemoveExisting(url, 'image')}
                              style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff', cursor: 'pointer', padding: 0, fontSize: 12, lineHeight: 1 }}>×</button>
                    </div>
                  ))}
                  {(existingMedia.videos || []).map((url, i) => (
                    <div key={'ev-' + i} style={{ position: 'relative', width: 72, height: 72, borderRadius: 6, overflow: 'hidden', background: '#000' }}>
                      <video src={url} muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <span style={{ position: 'absolute', top: 2, left: 2, padding: '0 5px', background: 'rgba(0,0,0,0.55)', color: '#fff', borderRadius: 3, fontSize: 9, fontWeight: 600 }}>VID</span>
                      <button type="button" onClick={() => handleRemoveExisting(url, 'video')}
                              style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff', cursor: 'pointer', padding: 0, fontSize: 12, lineHeight: 1 }}>×</button>
                    </div>
                  ))}
                  {/* Newly chosen File objects (not yet uploaded) */}
                  {postImage.map((file, i) => {
                    const isImg = (file.type || '').includes('image');
                    return (
                      <div key={'n-' + i + file.name} style={{ position: 'relative', width: 72, height: 72, borderRadius: 6, overflow: 'hidden', background: '#000' }}>
                        {isImg ? (
                          <img src={URL.createObjectURL(file)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <video src={URL.createObjectURL(file)} muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        )}
                        <span style={{ position: 'absolute', top: 2, left: 2, padding: '0 5px', background: 'rgba(15,98,254,0.85)', color: '#fff', borderRadius: 3, fontSize: 9, fontWeight: 600 }}>NEW</span>
                        <button type="button" onClick={() => handleRemoveUpload(file.name)}
                                style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff', cursor: 'pointer', padding: 0, fontSize: 12, lineHeight: 1 }}>×</button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>)}
          </div>

          <div className="preview-section">
          <div className="section-title">Attach Idea</div>
          <div className='date-time-section'>
            <select name="ideas" id="ideas" value={selectedIdea} onChange={({target}) => setSelectedIdea(target.value)}>
              <option value='' hidden>Select an Idea</option>
              {ideasRedux.map(x => <option value={x._id}>{x.title}</option>)}
            </select>
          </div>
            <div className="preview-header">
              <span>Post Preview</span>
              <select 
                className="platform-select"
                value={selectedPlatform}
                onChange={(e) => {
                  handlePlatformChange(e.target.value)
                  const account = connectedAccounts.find(x => x.platform === e.target.value);
                  setAccountData(account);
                }}
              >
                {connectedAccounts.map(platform => (
                  <option 
                    key={platform.platform} 
                    value={platform.platform}
                    disabled={platform.status !== 'active'}
                  >
                    {platform.name}
                  </option>
                ))}
              </select>
            </div>
            {selectedPlatform === 'tiktok' && (
              <TikTokPostOptions
                brandId={selectedBrandRedux?._id}
                mediaType={(postImage || []).some((f) => String(f?.type || '').startsWith('video')) ? 'VIDEO' : 'PHOTO'}
                onChange={setTiktokChoice}
                fetchCreatorInfo={fetchTikTokCreatorInfo}
              />
            )}
            {renderPreview()}
          </div>
        </div>
      </MDBModalBody>

      <div className="modal-footer">
        <button 
          className="btn-cancel" 
          onClick={handleClose}
        >
          Cancel
        </button>
        <button 
          className="btn-draft"
          onClick={() => handleSubmit('draft')}
          disabled={createLoading || uploadLoading}
        >
          Save Draft
        </button>
        <button 
          className="btn-schedule"
          onClick={() => handleSubmit()}
          disabled={createLoading || uploadLoading || !scheduleDate}
        >
          {createLoading || uploadLoading ? <Loading /> : 'Schedule'}
        </button>
      </div>
    </MDBModal>
  );
};

export default CreatePostModal;